/**
 * NIP-34 Actions — CreateIssue, ChangeIssueStatus, RenameIssueSubject, CreateComment, DeleteEvent.
 *
 * Relay strategy — callers declare intent via group ID strings; the outbox
 * store resolves them to relay URLs via its relayGroupResolver:
 *
 *   - "outbox:<signerPubkey>"  → user's NIP-65 write relays
 *   - "30617:<pubkey>:<d>"     → repo's declared relays (one per coord)
 *   - "inbox:<notifyPubkey>"   → notification recipient's NIP-65 read relays
 *                                (resolved lazily; retried when kind:10002 arrives)
 *
 * Deletion requests (kind:5) mirror the relay groups of the original event:
 * outbox + repo relays + inbox of any p/P-tagged pubkeys on the deleted events.
 *
 * The git index (wss://index.ngit.dev) is intentionally NOT a publish target —
 * it syncs from other relays and should not receive direct publishes.
 *
 * --- Applesauce v6 note ---
 * Actions receive a `signer` in the context (not a `factory`). Event drafts
 * are built with typed factory classes from `@/factories/` (IssueFactory,
 * StatusChangeFactory, etc.) and finalised with `.sign(signer)`.
 */

import type { Action } from "applesauce-actions";
import type { NostrEvent } from "nostr-tools";
import { CommentFactory, ReactionFactory } from "applesauce-common/factories";

import { IssueFactory, type IssueOptions } from "@/factories/IssueFactory";
import {
  StatusChangeFactory,
  STATUS_KIND_MAP,
} from "@/factories/StatusChangeFactory";
import { IssueLabelFactory } from "@/factories/IssueLabelFactory";
import { IssueSubjectRenameFactory } from "@/factories/IssueSubjectRenameFactory";
import {
  CoverNoteFactory,
  type CoverNoteOptions,
} from "@/factories/CoverNoteFactory";
import { DeletionFactory } from "@/factories/DeletionFactory";
import {
  InlineCommentFactory,
  type InlineCommentLocation,
} from "@/factories/InlineCommentFactory";
import { CIManualTriggerFactory } from "@/factories/CIManualTriggerFactory";

import type { IssueStatus } from "@/lib/nip34";
import { outboxStore } from "@/services/outbox";
import { eventStore } from "@/services/nostr";

// ---------------------------------------------------------------------------
// Relay group ID helpers
// ---------------------------------------------------------------------------

/**
 * Build the group ID strings for a publish call.
 *
 * Always includes:
 *   - "outbox:<signerPubkey>" → user's NIP-65 write relays
 *   - "30617:<pubkey>:<d>"    → repo's declared relays (one per coord)
 *
 * Optionally includes notification inbox groups:
 *   - "inbox:<pubkey>" → recipient's NIP-65 read relays (resolved lazily)
 */
function buildGroupIds(
  signerPubkey: string,
  repoCoords?: string[],
  notifyPubkeys?: string[],
): string[] {
  const ids: string[] = [`outbox:${signerPubkey}`];
  for (const coord of repoCoords ?? []) ids.push(coord);
  for (const pubkey of notifyPubkeys ?? []) ids.push(`inbox:${pubkey}`);
  return ids;
}

// ---------------------------------------------------------------------------
// Shared options
// ---------------------------------------------------------------------------

/** Extended options for CreateComment — adds extraTags on top of CommentFactoryOptions. */
export interface CreateCommentOptions {
  /** NIP-31 alt tag override */
  alt?: string;
  /** NIP-40 expiration timestamp */
  expiration?: number;
  /**
   * Additional raw tags to append verbatim (e.g. NIP-94 `imeta` tags from
   * Blossom uploads). Each element is a tag tuple like `["imeta", "url ...", ...]`.
   */
  extraTags?: string[][];
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * Create a NIP-34 git issue (kind:1621).
 *
 * Publishes to: user outbox + repo relays + repo owner's inbox (deferred).
 */
export function CreateIssue(
  repoCoord: string,
  ownerPubkey: string,
  subject: string,
  content: string,
  options?: IssueOptions,
): Action {
  return async ({ signer, self }) => {
    const signed = await IssueFactory.create(
      repoCoord,
      ownerPubkey,
      subject,
      content,
      options,
    ).sign(signer);

    // Add to local store immediately so the UI reflects the new issue without
    // waiting for a relay round-trip.
    eventStore.add(signed);

    const notifyPubkeys = ownerPubkey !== self ? [ownerPubkey] : [];
    // Fire-and-forget: publishing to the outbox can continue in the background
    // after the event is signed and added to the local store.
    outboxStore
      .publish(signed, buildGroupIds(self, [repoCoord], notifyPubkeys))
      .catch(console.error);
  };
}

/**
 * Change the status of a NIP-34 issue or PR (kinds 1630–1633).
 *
 * Publishes to: user outbox + repo relays + item author's & repo owners' inboxes (deferred).
 */
export function ChangeIssueStatus(
  itemId: string,
  itemAuthorPubkey: string,
  repoCoords: string[],
  nextStatus: Exclude<IssueStatus, "deleted">,
): Action {
  return async ({ signer, self }) => {
    const statusKind = STATUS_KIND_MAP[nextStatus];
    const signed = await StatusChangeFactory.create(
      statusKind,
      itemId,
      repoCoords,
      itemAuthorPubkey,
      self,
    ).sign(signer);

    // Add to local store immediately so the UI reflects the status change
    // without waiting for a relay round-trip.
    eventStore.add(signed);

    const repoOwners = repoCoords
      .map((c) => c.split(":")[1])
      .filter((pk): pk is string => !!pk);
    const notifyPubkeys = [
      ...new Set([itemAuthorPubkey, ...repoOwners].filter((pk) => pk !== self)),
    ];
    // Fire-and-forget: publishing to the outbox can continue in the background.
    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, notifyPubkeys))
      .catch(console.error);
  };
}

/**
 * Ask the coordinator of a completed CI workflow result to run it again.
 *
 * Authorization is enforced by the coordinator against the resolved repository
 * maintainer set. The UI only exposes this action to confirmed maintainers.
 */
export function TriggerManualCI(workflowResult: NostrEvent): Action {
  return async ({ signer, self }) => {
    const coordinatorPubkey = workflowResult.pubkey;
    const repoCoords = workflowResult.tags
      .filter(([name, value]) => name === "a" && !!value)
      .map(([, value]) => value);
    const signed = await CIManualTriggerFactory.create(
      workflowResult,
      coordinatorPubkey,
    ).sign(signer);

    eventStore.add(signed);
    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, [coordinatorPubkey]))
      .catch(console.error);
  };
}

/**
 * Rename a NIP-34 issue subject via a NIP-32 label event (kind:1985).
 *
 * Publishes to: user outbox + repo relays + item author's inbox (deferred).
 */
export function RenameIssueSubject(
  issueId: string,
  newSubject: string,
  repoCoords?: string[],
  issueAuthorPubkey?: string,
): Action {
  return async ({ signer, self }) => {
    const signed = await IssueSubjectRenameFactory.create(
      issueId,
      newSubject,
    ).sign(signer);

    // Add to local store immediately so the rename is reflected in the UI
    // without waiting for a relay round-trip.
    eventStore.add(signed);

    const notifyPubkeys =
      issueAuthorPubkey && issueAuthorPubkey !== self
        ? [issueAuthorPubkey]
        : [];
    // Fire-and-forget: publishing to the outbox can continue in the background.
    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, notifyPubkeys))
      .catch(console.error);
  };
}

/**
 * Attach labels to a NIP-34 issue via a NIP-32 label event (kind:1985).
 *
 * Publishes to: user outbox + repo relays + item author's inbox (deferred).
 */
export function AttachIssueLabels(
  issueId: string,
  labels: string[],
  repoCoords?: string[],
  issueAuthorPubkey?: string,
): Action {
  return async ({ signer, self }) => {
    const signed = await IssueLabelFactory.create(issueId, labels).sign(signer);

    // Add to local store immediately so the label change is reflected in the
    // UI without waiting for a relay round-trip.
    eventStore.add(signed);

    const notifyPubkeys =
      issueAuthorPubkey && issueAuthorPubkey !== self
        ? [issueAuthorPubkey]
        : [];
    // Fire-and-forget: publishing to the outbox can continue in the background.
    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, notifyPubkeys))
      .catch(console.error);
  };
}

/**
 * React to a NIP-34 event (issue, PR, patch, or comment) with a kind:7
 * reaction event.
 *
 * Publishes to: user outbox + repo relays + event author's inbox (deferred).
 *
 * @param targetEvent - The event being reacted to
 * @param emoji       - Reaction emoji (defaults to "+")
 * @param repoCoords  - Repo coordinate strings for relay group keying
 */
export function CreateReaction(
  targetEvent: NostrEvent,
  emoji: string,
  repoCoords?: string[],
): Action {
  return async ({ signer, self }) => {
    const signed = await ReactionFactory.create(targetEvent, emoji).sign(
      signer,
    );

    // Add to local store immediately for optimistic UI
    eventStore.add(signed);

    const notifyPubkeys =
      targetEvent.pubkey !== self ? [targetEvent.pubkey] : [];
    // Fire-and-forget: publishing to the outbox can continue in the background.
    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, notifyPubkeys))
      .catch(console.error);
  };
}

/**
 * Post a NIP-22 comment (kind:1111) on a NIP-34 issue, PR/patch, or an
 * existing comment.
 *
 * Publishes to: user outbox + repo relays + root event author's inbox +
 * parent comment author's inbox (both deferred via outbox re-resolution).
 *
 * @param parent    - The event being commented on (root issue/PR or a comment)
 * @param content   - Markdown body of the comment
 * @param rootEvent - The root issue/PR/patch event — used to notify its author
 *                    when `parent` is a reply-to-comment rather than the root itself
 * @param options   - Optional options (alt, expiration, extraTags, etc.)
 */
export function CreateComment(
  parent: NostrEvent,
  content: string,
  rootEvent?: NostrEvent,
  options?: CreateCommentOptions,
): Action {
  return async ({ signer, self }) => {
    const { extraTags, alt, expiration } = options ?? {};

    let factory = CommentFactory.create(parent, content, {
      alt,
      expiration,
    });

    // Append imeta / extra tags that CommentFactory doesn't handle natively
    if (extraTags && extraTags.length > 0) {
      factory = factory.modifyPublicTags((tags) => [...tags, ...extraTags]);
    }

    // Strip trailing slashes from relay hints on pointer tags. The URL
    // constructor used by applesauce's normalizeURL appends a trailing slash
    // to bare-host URLs (e.g. "wss://relay.ngit.dev/"), and root E tags
    // propagated from existing comments carry whatever hint was stored there.
    // Some relays reject events whose relay hints don't match their own URL.
    const POINTER_TAGS = new Set(["E", "e", "A", "a"]);
    factory = factory.modifyPublicTags((tags) =>
      tags.map((tag) => {
        if (POINTER_TAGS.has(tag[0]) && tag[2]) {
          return [tag[0], tag[1], tag[2].replace(/\/$/, ""), ...tag.slice(3)];
        }
        return tag;
      }),
    );

    const signed = await factory.sign(signer);

    // Add to local store immediately so the comment appears in the thread
    // without waiting for a relay round-trip.
    eventStore.add(signed);

    // Prefer the root event's 'a' tag (always the issue/PR); fall back to
    // the parent's 'a' tag. Comments (kind:1111) don't have 'a' tags so
    // without this we'd lose the coord when replying to a comment.
    const rootRepoCoord = (rootEvent ?? parent).tags.find(
      ([t]) => t === "a",
    )?.[1];
    const repoCoords = rootRepoCoord ? [rootRepoCoord] : undefined;

    // Notify the root event author and (if different) the parent comment
    // author. rootEvent.pubkey is the PR/patch/issue author; when parent IS
    // the root they're the same person.
    const rootPubkey = rootEvent?.pubkey ?? parent.pubkey;

    // Also notify any pubkeys @mentioned in the comment content. The
    // CommentFactory.text() adds lowercase "p" tags for nostr: mentions via
    // setShortTextContent — collect those so their inboxes receive the event.
    const mentionedPubkeys = signed.tags
      .filter(([t]) => t === "p")
      .map(([, pk]) => pk)
      .filter((pk): pk is string => !!pk);

    const notifyPubkeys = [
      ...new Set(
        [rootPubkey, parent.pubkey, ...mentionedPubkeys].filter(
          (pk) => pk !== self,
        ),
      ),
    ];

    // Fire-and-forget: publishing to the outbox can continue in the background.
    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, notifyPubkeys))
      .catch(console.error);
  };
}

/**
 * Create or update a cover note (kind:1624) for a NIP-34 issue or PR.
 *
 * A cover note is a pinned note posted by the item author or a maintainer
 * that appears above the first description card on an issue or PR page.
 *
 * Publishes to: user outbox + repo relays + item author's inbox (deferred).
 *
 * @param rootEvent  - The root issue / PR / patch event being annotated
 * @param content    - Markdown body of the cover note
 * @param repoCoords - Repo coordinate strings for relay group keying
 * @param options    - Optional: extraTags (e.g. imeta from Blossom uploads)
 */
export function CreateCoverNote(
  rootEvent: NostrEvent,
  content: string,
  repoCoords?: string[],
  options?: CoverNoteOptions,
): Action {
  return async ({ signer, self }) => {
    const signed = await CoverNoteFactory.create(
      rootEvent,
      content,
      options,
    ).sign(signer);

    // Add to local store immediately so the cover note appears without
    // waiting for a relay round-trip.
    eventStore.add(signed);

    const notifyPubkeys = rootEvent.pubkey !== self ? [rootEvent.pubkey] : [];
    // Fire-and-forget: publishing to the outbox can continue in the background.
    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, notifyPubkeys))
      .catch(console.error);
  };
}

/**
 * Send a NIP-09 deletion request (kind:5) for one or more events.
 *
 * The deletion request is published to the same relay groups as the original
 * event was published to, so every relay holding the event receives the request:
 *   - user outbox (always)
 *   - repo relays (when repoCoords are provided)
 *   - inbox relays of any pubkeys tagged in the original events — these are
 *     the same notification targets that received the event, derived from the
 *     p/P tags on the events being deleted (e.g. root author on comments,
 *     target author on reactions, item author + repo owners on status changes).
 *
 * @param events     - The event(s) to request deletion of (must be authored by self)
 * @param repoCoords - Repo coordinate strings for relay group keying
 * @param reason     - Optional human-readable reason (written to content field)
 */
export function DeleteEvent(
  events: NostrEvent[],
  repoCoords?: string[],
  reason?: string,
): Action {
  return async ({ signer, self }) => {
    // Validate: all events must be authored by the signer
    for (const ev of events) {
      if (ev.pubkey !== self) {
        throw new Error(
          `Cannot delete event ${ev.id.slice(0, 8)}: not authored by current account`,
        );
      }
    }

    const signed = await DeletionFactory.forEvents(events, reason).sign(signer);

    // Add to local store immediately so the UI can react
    eventStore.add(signed);

    // Derive notification pubkeys from the events being deleted.
    // The original publish sent to inbox:<pubkey> for every p/P tag on the
    // event (e.g. root author on comments, target author on reactions, item
    // author + repo owners on status changes). We must reach those same relays
    // with the deletion request.
    const notifyPubkeys = new Set<string>();
    for (const ev of events) {
      for (const [t, pk] of ev.tags) {
        if ((t === "p" || t === "P") && pk && pk !== self) {
          notifyPubkeys.add(pk);
        }
      }
    }

    // Fire-and-forget: publishing to the outbox can continue in the background.
    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, [...notifyPubkeys]))
      .catch(console.error);
  };
}

/**
 * Resolve a comment thread by posting a kind:1111 reply with `["l", "resolved"]`.
 *
 * The thread root is the first inline comment (or any NIP-22 comment) that
 * started the sub-thread. The resolution event uses the same NIP-22 root
 * (E/K/P) as the rest of the thread, with the thread root as the parent.
 *
 * Gating (maintainer or PR/patch author) is enforced in the UI; this action
 * does not re-check — callers are responsible for the gate.
 *
 * @param prOrPatchEvent  - The root PR (kind:1618) or patch (kind:1617)
 * @param threadRootEvent - The first comment in the sub-thread being resolved
 * @param repoCoords      - Repo coordinate strings for relay group keying
 */
export function ResolveThread(
  prOrPatchEvent: NostrEvent,
  threadRootEvent: NostrEvent,
  repoCoords?: string[],
): Action {
  return async ({ sign, self }) => {
    const relay = "";
    const tags: string[][] = [
      // NIP-22 root — always the PR or patch
      ["E", prOrPatchEvent.id, relay, prOrPatchEvent.pubkey],
      ["K", String(prOrPatchEvent.kind)],
      ["P", prOrPatchEvent.pubkey, relay],

      // NIP-22 parent — the sub-thread root being resolved
      ["e", threadRootEvent.id, relay, threadRootEvent.pubkey],
      ["k", String(threadRootEvent.kind)],
      ["p", threadRootEvent.pubkey],

      // Resolution state
      ["l", "resolved"],
    ];

    const draft = {
      kind: 1111 as const,
      content: "marked as resolved",
      tags,
      created_at: Math.floor(Date.now() / 1000),
    };

    const signed = await sign(draft);
    eventStore.add(signed);

    const notifyPubkeys = [
      ...new Set(
        [prOrPatchEvent.pubkey, threadRootEvent.pubkey].filter(
          (pk) => pk !== self,
        ),
      ),
    ];

    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, notifyPubkeys))
      .catch(console.error);
  };
}

/**
 * Post an inline code review comment (kind:1111) on a NIP-34 PR or patch.
 *
 * Extends the standard NIP-22 comment with file/line/commit location tags as
 * defined in NIP.md. Publishes to the same relay groups as CreateComment.
 *
 * @param rootEvent    - The PR (kind:1618) or patch (kind:1617) being reviewed
 * @param parentEvent  - Immediate parent (same as rootEvent, or a PR update)
 * @param content      - Comment body
 * @param options      - Code location: filePath, commitId, line, repoCoords
 */
export function CreateInlineComment(
  rootEvent: NostrEvent,
  parentEvent: NostrEvent,
  content: string,
  options: InlineCommentLocation,
): Action {
  return async ({ signer, self }) => {
    const signed = await InlineCommentFactory.forLocation(
      rootEvent,
      parentEvent,
      content,
      options,
    ).sign(signer);

    // Add to local store immediately so the comment appears without a relay round-trip.
    eventStore.add(signed);

    const repoCoords = options.repoCoords;
    const notifyPubkeys = [
      ...new Set(
        [rootEvent.pubkey, parentEvent.pubkey].filter((pk) => pk !== self),
      ),
    ];

    outboxStore
      .publish(signed, buildGroupIds(self, repoCoords, notifyPubkeys))
      .catch(console.error);
  };
}

/**
 * Send a NIP-09 deletion request (kind:5) for a repository announcement.
 *
 * Two modes:
 *   - "version": deletes only the specific event version via an `e` tag
 *   - "repo":    deletes the entire repository (all versions) via an `a` tag
 *
 * @param announcement - The current announcement event (kind:30617)
 * @param mode         - "version" to delete just this event; "repo" to delete all versions
 * @param repoCoords   - Repo coordinate strings for relay group keying
 * @param reason       - Optional human-readable reason
 */
export function DeleteRepo(
  announcement: NostrEvent,
  mode: "version" | "repo",
  repoCoords?: string[],
  reason?: string,
): Action {
  return async ({ signer, self }) => {
    if (announcement.pubkey !== self) {
      throw new Error(
        `Cannot delete repo announcement ${announcement.id.slice(0, 8)}: not authored by current account`,
      );
    }

    const factory =
      mode === "version"
        ? // Delete only this specific event version via `e` tag
          DeletionFactory.forEvents([announcement], reason)
        : // Delete the entire repository (all versions) via `a` tag
          (() => {
            const dTag = announcement.tags.find(([t]) => t === "d")?.[1] ?? "";
            const aCoord = `${announcement.kind}:${announcement.pubkey}:${dTag}`;
            return DeletionFactory.forAddressable(
              aCoord,
              announcement.kind,
              reason,
            );
          })();

    const signed = await factory.sign(signer);

    // Publish to user outbox + repo relays + git index relay.
    // The git index (wss://index.ngit.dev) holds repo announcements and must
    // receive public deletion requests so it stops serving the event to other
    // clients. Publish before applying the deletion locally so the privacy
    // boundary can still inspect the announcement being deleted.
    await outboxStore.publish(signed, [
      ...buildGroupIds(self, repoCoords),
      "git-index",
    ]);

    // Apply the optimistic deletion only after the publication boundary has
    // classified it against the current repository announcement.
    eventStore.add(signed);
  };
}
